<template>
  <div>
    <v-form @submit.prevent="submitEvent">
      <!-- STEPPER TO BETTER VISUALIZE THE STEPS -->
      <v-stepper
          v-model="currentFormStep"
          border="primary"
          alt-labels>

        <!-- STEPPER HEADERS WITH TWO STEPS -->
        <v-stepper-header
            class="elevation-0 bg-header-gradient">

          <!-- FIRST STEP HEADER (PSI/CALL IMPACT) -->
          <v-stepper-item
              title="PSI Name & Call Impact"
              :complete="currentFormStep > 0"
              :error="messages.length !== 0 && currentFormStep === 0"
              color="blue-darken-1"
              value="1" />

          <!-- STRAIGHT LINE -->
          <v-divider />

          <!-- SECOND STEP HEADER -->
          <v-stepper-item
              title="Margins"
              :complete="currentFormStep > 1"
              :error="messages.length !== 0 && currentFormStep === 0"
              color="blue-darken-4"
              value="2" />
        </v-stepper-header>

        <!-- DIVIDER BETWEEN THE HEADERS & MAIN CONTENT -->
        <v-divider />

        <!-- MAIN CONTENT OF STEPPER (THE ACTUAL FORM) -->
        <v-stepper-window class="px-10 mt-8">

          <!-- FIRST STEP -->
          <v-stepper-window-item
              value="1"
              :reverse-transition="false"
              :transition="false" >
            <FirstStep
                v-if="currentFormStep === 0"
                v-model="form"
                :psi-types="psiTypes"
                @name-changed="onNameChange"
                class="mt-6" />

          </v-stepper-window-item>

          <!-- SECOND STEP -->
          <v-stepper-window-item
              value="2"
              :reverse-transition="false"
              :transition="false"
          >
            <SecondStep
                v-if="currentFormStep === 1"
                v-model="form"
                class="mt-6" />
          </v-stepper-window-item>

        </v-stepper-window>

        <!-- DIVIDER BETWEEN THE MAIN CONTENT & THE BUTTONS -->
        <v-divider class="mb-4 mt-5" />

        <v-stepper-actions>
          <template v-slot:prev>
            <v-btn
                text="Previous"
                rounded
                prepend-icon="fa fa-arrow-left"
                color="warning"
                variant="outlined"
                @click.prevent="back"
                @click="$event.target.blur()" />
          </template>

          <template v-slot:next>
            <v-btn
                text="Next"
                rounded
                append-icon="fa fa-arrow-right"
                color="green"
                variant="outlined"
                @click="$event.target.blur()"
                @click.prevent="proceed" />
          </template>

        </v-stepper-actions>

      </v-stepper>

      <div v-if="messages.length > 0" class="my-2">
        <v-alert
            color="red"
            type="error"
            variant="tonal"
            dismissible
            class="my-2"
            v-for="(message, index) in messages"
            :key="`message${index}`">
          {{ message }}
        </v-alert>
      </div>

      <v-row>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
              v-if="currentFormStep === 1"
              type="submit"
              text="SUBMIT"
              color="green"
              rounded
              variant="flat"
              class="mt-5" />
        </v-col>

      </v-row>
    </v-form>
  </div>

</template>

<script setup>
import FirstStep from "./FormSteps/FirstStep.vue";
import SecondStep from "./FormSteps/SecondStep.vue";
import LinkButton from "../../Shared/LinkButton.vue";
import Utils from "../../Mixins/Utils";
import {ref} from "vue";

const emit = defineEmits(['submit'])

const props = defineProps({
  projectId: {
    type: Number,
    required: true
  },
  modelValue: {
    type: Object,
    required: true
  },
  psiTypes: {
    value: Array,
    required: true
  },
  defaultPsiValues: {
    type: Object,
    required: false
  }
})

const form = props.modelValue

const currentFormStep = ref(0)
const formSteps = ['PSI & Call Impact Selection', 'Margins']
const messages = ref([])

function proceed() {
  if (currentFormStep.value === 0) proceedFirstStep()

  if (messages.value.length === 0)
    currentFormStep.value += 1
}

function back() {
  currentFormStep.value -= 1
}

function proceedFirstStep() {
  messages.value = []
  if (form.name === null) messages.value.push('Empty PSI name.')
  // if (this.form.call_impact === null) this.messages.push('Empty Call Impact name.')
  if (!form.value) messages.value.push('Empty value.')
}

function proceedSecondStep () {
  messages.value = []
  if (!form.target_value) messages.value.push('Empty target value.')

  const isMonotonic = Utils.methods.isMonotonic([form.m0, form.m1, form.m2, form.m3])
  if (['error', 'null'].includes(isMonotonic))
    messages.value.push('Please provide correct margins.')
}

function submitEvent () {
  messages.value = []
  proceedSecondStep()
  if (messages.value.length === 0) {
    emit('submit')
  }
}

function onNameChange($event) {
  if (props.defaultPsiValues) {
    const name = $event
    const psi = form
    if(props.defaultPsiValues[name])
    {
      const defaultValues = props.defaultPsiValues[name]
      psi.value = defaultValues['value']
      psi.call_impact = defaultValues['call_impact']
      psi.target_value = defaultValues['target_value']
      psi.relative_margins = defaultValues['relative_margins']
      psi.m0 = defaultValues['m0']
      psi.m1 = defaultValues['m1']
      psi.m2 = defaultValues['m2']
      psi.m3 = defaultValues['m3']
    }

  }
}
</script>

<style scoped>
.bg-header-gradient {
  background: linear-gradient(to left, #cdeffa, #b2d0fa);
}

.v-stepper-header .v-stepper-item {
  font-size: 21px;
}
</style>
